import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../api/axios";
import QuotesTab from "./QuotesTab";
import AgreementTab from "./AgreementTab";
import OrdersTab from "./OrdersTab";
import FinancialsTab from "./FinancialsTab";
import InvoicesTab from "./InvoicesTab";
import ClientInventoryTab from "./ClientInventoryTab";

/** Origin for `/uploads/...` (API host without `/api`). */
const fileHostFromEnv = () => {
  const raw = (
    typeof process !== "undefined" &&
    process.env.REACT_APP_API_URL
  ) || "http://localhost:5000/api";
  const withoutApi = raw.replace(/\/api\/?$/, "");
  if (/^https?:\/\//i.test(withoutApi)) return withoutApi;
  if (withoutApi.startsWith("//")) return `${window.location.protocol}${withoutApi}`;
  if (withoutApi.startsWith("/")) return "http://localhost:5000";
  return `http://${withoutApi}`;
};
const FILE_HOST = fileHostFromEnv();

const FILE_CATS = [
  { key: "recce", label: "Recce" },
  { key: "2d-design", label: "2D Design" },
  { key: "3d-design", label: "3D Design" },
];

const getFileUrl = (file, { clientId } = {}) => {
  const url = file?.url || "";
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (clientId) {
    const oldPath = url.match(/^\/uploads\/client\/([^/]+)\/([^/]+)$/i);
    if (oldPath) {
      const [, type, filename] = oldPath;
      return `${FILE_HOST}/uploads/client-files/${clientId}/${type}/${filename}`;
    }
  }
  return `${FILE_HOST}${url.startsWith("/") ? url : `/${url}`}`;
};

const isImageFile = (file) => file?.mimetype?.startsWith("image/");
const isPdfFile = (file) =>
  file?.mimetype === "application/pdf" ||
  file?.filename?.toLowerCase().endsWith(".pdf") ||
  file?.url?.toLowerCase().endsWith(".pdf");

function FilesTab({ clientId }) {
  const [activeFileTab, setActiveFileTab] = useState("recce");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [deletingId, setDeletingId] = useState("");
  const [openFileMenuId, setOpenFileMenuId] = useState("");
  const fileRef = useRef();

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clients/${clientId}/files`, {
        params: { fileType: activeFileTab },
      });
      setFiles(res.data.data.files || []);
    } catch (e) {
      console.error(e);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, activeFileTab]);

  useEffect(() => {
    if (!clientId) return;
    fetchFiles();
  }, [fetchFiles, clientId]);

  useEffect(() => {
    const closeMenu = () => setOpenFileMenuId("");
    document.addEventListener("click", closeMenu);
    return () => document.removeEventListener("click", closeMenu);
  }, []);

  const handleUpload = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    try {
      for (const f of arr) {
        const fd = new FormData();
        // Multer reads req.body.fileType in destination() — fields must come *before* `file` in the stream
        fd.append("fileType", activeFileTab);
        fd.append("title", f.name || "Untitled");
        fd.append("file", f);
        await api.post(`/clients/${clientId}/files`, fd);
      }
      if (fileRef.current) fileRef.current.value = "";
      fetchFiles();
    } catch (e) {
      console.error(e);
      alert("Upload failed");
    }
  };

  const deleteFile = async (file) => {
    if (!window.confirm(`Delete ${file.title || file.filename || "this file"}?`)) return;
    setDeletingId(file._id);
    try {
      await api.delete(`/clients/${clientId}/files/${file._id}`);
      if (previewFile?._id === file._id) setPreviewFile(null);
      await fetchFiles();
    } catch (e) {
      console.error(e);
      alert("Delete failed");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {FILE_CATS.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveFileTab(cat.key)}
              style={{
                padding: "6px 12px",
                borderRadius: 20,
                border: "1px solid #E5E7EB",
                background: activeFileTab === cat.key ? "#976793" : "#fff",
                color: activeFileTab === cat.key ? "#fff" : "#374151",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => fileRef.current?.click()}
          style={{
            padding: "7px 16px",
            background: "#976793",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Upload Files
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "#9CA3AF" }}>
          Loading…
        </div>
      ) : files.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            color: "#9CA3AF",
            border: "1px solid #E5E7EB",
            borderRadius: 10,
            background: "#fff",
          }}
        >
          No files uploaded yet
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 12,
          }}
        >
          {files.map((f) => {
            const url = getFileUrl(f, { clientId });
            const image = isImageFile(f);
            const pdf = isPdfFile(f);
            return (
            <div
              key={f._id}
              style={{
                background: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: 10,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <button
                type="button"
                onClick={() => setPreviewFile(f)}
                style={{
                  width: "100%",
                  height: 140,
                  border: "none",
                  padding: 0,
                  background: "#F9FAFB",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {image ? (
                  <img
                    src={url}
                    alt={f.title || f.filename || "file"}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div style={{ textAlign: "center", color: pdf ? "#EF4444" : "#6B7280" }}>
                    <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }}>
                      {pdf ? "PDF" : "FILE"}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 8, color: "#9CA3AF" }}>
                      Click to preview
                    </div>
                  </div>
                )}
              </button>
              <div style={{ position: "absolute", top: 8, right: 8 }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenFileMenuId((id) => (id === f._id ? "" : f._id));
                  }}
                  title="File actions"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    background: "rgba(255,255,255,0.94)",
                    color: "#374151",
                    border: "1px solid #E5E7EB",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    fontSize: 18,
                    fontWeight: 700,
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ⋮
                </button>
                {openFileMenuId === f._id && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: "absolute",
                      top: 34,
                      right: 0,
                      width: 130,
                      background: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      boxShadow: "0 10px 30px rgba(17,24,39,0.14)",
                      overflow: "hidden",
                      zIndex: 5,
                    }}
                  >
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "block",
                        padding: "9px 12px",
                        color: "#374151",
                        textDecoration: "none",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Open
                    </a>
                    <button
                      type="button"
                      onClick={() => deleteFile(f)}
                      disabled={deletingId === f._id}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "9px 12px",
                        color: "#EF4444",
                        background: "#fff",
                        border: "none",
                        borderTop: "1px solid #F3F4F6",
                        textAlign: "left",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: deletingId === f._id ? "not-allowed" : "pointer",
                      }}
                    >
                      {deletingId === f._id ? "Removing..." : "Remove"}
                    </button>
                  </div>
                )}
              </div>
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#1F2937" }}>
                  {f.title || "Untitled"}
                </div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                  {f.filename}
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {previewFile && (
        <div
          onClick={() => setPreviewFile(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            background: "rgba(17,24,39,0.82)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(920px, 94vw)",
              maxHeight: "90vh",
              background: "#fff",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 24px 70px rgba(0,0,0,0.28)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 16px",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {previewFile.title || previewFile.filename || "Preview"}
                </div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{previewFile.filename}</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <a
                  href={getFileUrl(previewFile, { clientId })}
                  target="_blank"
                  rel="noreferrer"
                  style={{ padding: "7px 12px", border: "1px solid #E5E7EB", borderRadius: 8, color: "#374151", textDecoration: "none", fontSize: 12, fontWeight: 600 }}
                >
                  Open
                </a>
                <button
                  type="button"
                  onClick={() => deleteFile(previewFile)}
                  disabled={deletingId === previewFile._id}
                  style={{ padding: "7px 12px", border: "1px solid #FECACA", borderRadius: 8, color: "#EF4444", background: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                >
                  {deletingId === previewFile._id ? "Deleting..." : "Delete"}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewFile(null)}
                  style={{ width: 32, border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff", color: "#6B7280", fontSize: 16, cursor: "pointer" }}
                >
                  x
                </button>
              </div>
            </div>
            <div style={{ height: "min(72vh, 680px)", background: "#F9FAFB", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {isImageFile(previewFile) ? (
                <img src={getFileUrl(previewFile, { clientId })} alt={previewFile.title || previewFile.filename || "Preview"} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
              ) : isPdfFile(previewFile) ? (
                <iframe title={previewFile.title || "PDF preview"} src={getFileUrl(previewFile, { clientId })} style={{ width: "100%", height: "100%", border: 0 }} />
              ) : (
                <div style={{ textAlign: "center", color: "#6B7280", padding: 24 }}>
                  <div style={{ fontSize: 42, fontWeight: 800, marginBottom: 10 }}>FILE</div>
                  <div style={{ fontSize: 13, marginBottom: 12 }}>Preview is not available for this file type.</div>
                  <a href={getFileUrl(previewFile, { clientId })} target="_blank" rel="noreferrer" style={{ color: "#976793", fontWeight: 700 }}>
                    Open file
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotesTab({ clientId, projectId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const editorRef = useRef();
  const fileRef = useRef();
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [noteFiles, setNoteFiles] = useState({});
  const [cc, setCc] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCC, setShowCC] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/activity-logs", {
        params: { client: clientId, type: "note", limit: 100 },
      });
      setNotes(r.data.data.logs || []);
    } catch (e) {
      console.error(e);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    load();
  }, [load, clientId]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("/users");
        const userData = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : Array.isArray(res.data?.data?.users)
              ? res.data.data.users
              : [];
        setUsers(userData);
      } catch (e) {
        console.error(e);
        setUsers([]);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const close = () => setShowCC(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    if (!notes.length) {
      setNoteFiles({});
      return;
    }

    let isMounted = true;
    const loadLinkedFiles = async () => {
      try {
        const results = await Promise.all(
          notes.map(async (n) => {
            const res = await api.get(`/clients/${clientId}/files/by-link`, {
              params: { linkedTo: "note", linkedId: n._id },
            });
            return { id: n._id, files: res.data?.data?.files || [] };
          })
        );

        if (!isMounted) return;
        const map = {};
        results.forEach((r) => {
          map[r.id] = r.files;
        });
        setNoteFiles(map);
      } catch (e) {
        console.error(e);
      }
    };

    loadLinkedFiles();
    return () => {
      isMounted = false;
    };
  }, [notes, clientId]);

  const applyFormat = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
  };

  const submit = async () => {
    const body = editorRef.current?.innerHTML || "";
    const plainText = body.replace(/<[^>]*>/g, "").trim();
    if (!plainText) return;
    setSaving(true);
    try {
      const res = await api.post("/activity-logs", {
        title: plainText.slice(0, 80) || "Note",
        description: body,
        type: "note",
        client: clientId,
        project: projectId,
        cc,
      });
      const noteId = res.data?.data?.log?._id;
      if (noteId && files.length > 0) {
        for (const file of files) {
          const fd = new FormData();
          fd.append("fileType", "recce");
          fd.append("title", file.name || "Untitled");
          fd.append("linkedTo", "note");
          fd.append("linkedId", noteId);
          fd.append("file", file);
          await api.post(`/clients/${clientId}/files`, fd);
        }
      }
      if (editorRef.current) {
        editorRef.current.innerHTML = "";
      }
      setFiles([]);
      setCc([]);
      load();
    } catch (e) {
      console.error(e);
      alert("Error saving note");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 780 }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: 12,
          overflow: "hidden",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", gap: 2, padding: "6px 10px", borderBottom: "1px solid #E5E7EB", background: "#FAFAFA", flexWrap: "wrap" }}>
          {[
            { label: "B", action: () => document.execCommand("bold") },
            { label: "I", action: () => document.execCommand("italic") },
            { label: "U", action: () => document.execCommand("underline") },
            { label: "S", action: () => document.execCommand("strikeThrough") },
            { label: "P", action: () => applyFormat("formatBlock", "p") },
            { label: "H1", action: () => applyFormat("formatBlock", "h1") },
            { label: "H2", action: () => applyFormat("formatBlock", "h2") },
            { label: "H3", action: () => applyFormat("formatBlock", "h3") },
            { label: "•", action: () => document.execCommand("insertUnorderedList") },
            { label: "1.", action: () => document.execCommand("insertOrderedList") },
            { label: "\"", action: () => applyFormat("formatBlock", "blockquote") },
            { label: "—", action: () => document.execCommand("insertHorizontalRule") },
          ].map((btn) => (
            <button
              key={btn.label}
              onMouseDown={(e) => {
                e.preventDefault();
                btn.action();
              }}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 12,
                color: "#374151",
                borderRadius: 4,
                padding: "4px 7px",
              }}
            >
              {btn.label}
            </button>
          ))}
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          style={{
            minHeight: 90,
            padding: "14px 16px",
            fontSize: 13,
            color: "#374151",
            outline: "none",
            lineHeight: 1.7,
          }}
        />
        <div style={{ borderTop: "1px solid #E5E7EB", padding: "10px 16px" }}>
          <div style={{ marginBottom: 12, position: "relative" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>CC</div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowCC((prev) => !prev);
              }}
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 12,
                background: "#F9FAFB",
                color: "#374151",
                cursor: "pointer",
              }}
            >
              {cc.length === 0
                ? "Select users..."
                : `${cc.length} selected`}
            </div>
            {showCC && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  marginTop: 6,
                  width: "100%",
                  maxHeight: 220,
                  overflowY: "auto",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  background: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                  zIndex: 20,
                }}
              >
                {users.length === 0 ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: "#9CA3AF" }}>
                    No users available
                  </div>
                ) : (
                  users.map((u) => (
                    <label
                      key={u._id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "8px 10px",
                        fontSize: 12,
                        color: "#374151",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={cc.includes(u._id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCc((prev) => [...prev, u._id]);
                          } else {
                            setCc((prev) => prev.filter((id) => id !== u._id));
                          }
                        }}
                      />
                      <span>{u.name || u.email || "User"}</span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Files</div>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 8 }}>
            {files.length ? files.map((f) => f.name).join(", ") : "No files selected"}
          </div>
          <button
            onClick={() => fileRef.current?.click()}
            style={{
              padding: "6px 14px",
              background: "#976793",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Upload
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              const picked = Array.from(e.target.files || []);
              if (!picked.length) return;
              setFiles((prev) => [...prev, ...picked]);
              // Allow selecting the same file again later if needed.
              e.target.value = "";
            }}
          />
        </div>
        <div
          style={{
            borderTop: "1px solid #E5E7EB",
            padding: "10px 16px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={submit}
            disabled={saving}
            style={{
              padding: "7px 20px",
              background: saving ? "#9CA3AF" : "#976793",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: saving ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Saving…" : "Submit"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#976793" }}>All notes</div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search notes..."
          style={{
            width: 220,
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 12,
            outline: "none",
            background: "#fff",
          }}
        />
      </div>

      {loading ? (
        <div style={{ padding: "2rem", color: "#9CA3AF" }}>Loading…</div>
      ) : notes.filter((n) => {
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return (
          (n.title || "").toLowerCase().includes(q) ||
          (n.description || "").toLowerCase().includes(q)
        );
      }).length === 0 ? (
        <div style={{ padding: "2rem", color: "#9CA3AF" }}>No notes yet</div>
      ) : (
        notes
          .filter((n) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            return (
              (n.title || "").toLowerCase().includes(q) ||
              (n.description || "").toLowerCase().includes(q)
            );
          })
          .map((n) => (
          <div
            key={n._id}
            style={{
              background: "#fff",
              border: "1px solid #E5E7EB",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1F2937" }}>
                Note
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                {n.createdBy?.name || "User"}
              </div>
            </div>
            <div
              style={{ fontSize: 13, color: "#374151", lineHeight: 1.65 }}
              dangerouslySetInnerHTML={{ __html: n.description || "" }}
            />
            {!!n.cc?.length && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#6B7280" }}>
                <strong>cc:</strong>{" "}
                {n.cc.map((u) => (
                  <span
                    key={u._id}
                    style={{
                      marginRight: 6,
                      background: "#F7F1F7",
                      color: "#976793",
                      borderRadius: 20,
                      padding: "2px 8px",
                      fontSize: 11,
                    }}
                  >
                    {u.name || "User"}
                  </span>
                ))}
              </div>
            )}
            <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(noteFiles[n._id] || []).map((f) => (
                <img
                  key={f._id}
                  src={`http://localhost:5000${f.url}`}
                  alt={f.title || f.filename || "attachment"}
                  style={{
                    width: 120,
                    height: 90,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #E5E7EB",
                  }}
                />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8 }}>
              {new Date(n.createdAt).toLocaleString()}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const PROJECT_TAB_IDS = new Set(["files", "notes", "quotes", "agreement", "orders", "invoices", "financials", "inventory", "details"]);

function getEffectiveProjectTab(raw, isConverted) {
  const t = raw || "files";
  if (t === "payment") return "invoices";
  if (!PROJECT_TAB_IDS.has(t)) return "files";
  if (!isConverted && (t === "invoices" || t === "financials")) return "files";
  return t;
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConverted, setIsConverted] = useState(false);
  const [converting, setConverting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [detailsEdit, setDetailsEdit] = useState(null);
  const [detailsForm, setDetailsForm] = useState({});
  const [detailsSaving, setDetailsSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${id}`);
      setProject(r.data.data.project);
    } catch (e) {
      console.error(e);
      setProject(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!project?.client || typeof project.client !== "object") return;
    setIsConverted(project.client.status === "active");
  }, [project]);

  useEffect(() => {
    if (!showInfo) return;
    const close = () => setShowInfo(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [showInfo]);

  const rawTab = searchParams.get("tab") || "files";
  const activeTab = getEffectiveProjectTab(rawTab, isConverted);

  useEffect(() => {
    if (!project) return;
    const t = searchParams.get("tab") || "files";
    if (t === "payment" || (!isConverted && (t === "invoices" || t === "financials"))) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", t === "payment" ? "invoices" : "files");
          return next;
        },
        { replace: true }
      );
    }
  }, [project, isConverted, searchParams, setSearchParams]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <div
          style={{
            width: 32,
            height: 32,
            border: "3px solid #E5E7EB",
            borderTopColor: "#976793",
            borderRadius: "50%",
            animation: "spin 0.7s linear infinite",
          }}
        />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ padding: 20 }}>
        <button
          onClick={() => navigate("/projects")}
          style={{
            padding: "7px 16px",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Back
        </button>
        <div style={{ marginTop: 16, color: "#9CA3AF" }}>Project not found.</div>
      </div>
    );
  }

  const clientRaw = project.client?._id ?? project.client;
  const clientId = clientRaw != null && clientRaw !== "" ? String(clientRaw) : "";

  const tabs = [
    { id: "files", label: "Files" },
    { id: "notes", label: "Notes" },
    { id: "quotes", label: "Quotes" },
    { id: "agreement", label: "Agreement" },
    { id: "orders", label: "Orders" },
    { id: "inventory", label: "Inventory" },
    { id: "invoices", label: "Invoices", requiresConverted: true },
    { id: "financials", label: "Financials", requiresConverted: true },
    { id: "details", label: "Details" },
  ];

  const openDetailsEdit = (section) => {
    const client = typeof project.client === "object" ? project.client : {};
    const baseForm = {
      name: client?.name || project.clientName || "",
      email: client?.email || project.email || "",
      phone: client?.phone || project.phone || "",
      estBudget: project.estBudget ?? client?.estBudget ?? "",
      description: project.description || "",
      propertyAddress: project.propertyAddress || client?.propertyAddress || "",
      city: project.city || client?.address?.city || "",
      state: project.state || client?.address?.state || "",
      pincode: client?.address?.pincode || "",
      location: client?.address?.location || "",
      possessionMonth: project.possessionMonth || client?.possessionMonth || "",
      bhkType: project.bhkType || client?.bhkType || "",
      stage: project.stage || client?.stage || "Lead Received",
    };
    setDetailsForm(baseForm);
    setDetailsEdit(section);
  };

  const updateDetailsField = (field, value) => {
    setDetailsForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitDetailsEdit = async (e) => {
    e.preventDefault();
    if (!detailsEdit) return;
    setDetailsSaving(true);
    try {
      const projectPayload = {};
      const clientPayload = {};

      if (detailsEdit === "contact") {
        projectPayload.clientName = detailsForm.name;
        projectPayload.title = detailsForm.name;
        projectPayload.email = detailsForm.email;
        projectPayload.phone = detailsForm.phone;
        clientPayload.name = detailsForm.name;
        clientPayload.email = detailsForm.email;
        clientPayload.phone = detailsForm.phone;
      }

      if (detailsEdit === "project") {
        projectPayload.estBudget = detailsForm.estBudget;
        projectPayload.description = detailsForm.description;
        clientPayload.estBudget = detailsForm.estBudget;
      }

      if (detailsEdit === "address") {
        projectPayload.propertyAddress = detailsForm.propertyAddress;
        projectPayload.city = detailsForm.city;
        projectPayload.state = detailsForm.state;
        projectPayload.addressLine1 = detailsForm.propertyAddress;
        projectPayload.pincode = detailsForm.pincode;
        projectPayload.location = detailsForm.location;
        clientPayload.propertyAddress = detailsForm.propertyAddress;
        clientPayload.address = {
          ...(project.client?.address || {}),
          line1: detailsForm.propertyAddress,
          street: detailsForm.propertyAddress,
          city: detailsForm.city,
          state: detailsForm.state,
          pincode: detailsForm.pincode,
          location: detailsForm.location,
        };
      }

      if (detailsEdit === "additional") {
        projectPayload.possessionMonth = detailsForm.possessionMonth;
        projectPayload.bhkType = detailsForm.bhkType;
        projectPayload.stage = detailsForm.stage;
        projectPayload.estBudget = detailsForm.estBudget;
        clientPayload.possessionMonth = detailsForm.possessionMonth;
        clientPayload.bhkType = detailsForm.bhkType;
        clientPayload.stage = detailsForm.stage;
        clientPayload.estBudget = detailsForm.estBudget;
      }

      if (Object.keys(projectPayload).length) {
        await api.put(`/projects/${project._id}`, projectPayload);
      }
      if (clientId && Object.keys(clientPayload).length) {
        await api.put(`/clients/${clientId}`, clientPayload);
      }
      setDetailsEdit(null);
      await load();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Could not update details";
      alert(message);
    } finally {
      setDetailsSaving(false);
    }
  };

  const detailsEditFields = {
    contact: [
      ["name", "Name"],
      ["email", "Email"],
      ["phone", "Phone"],
    ],
    project: [
      ["estBudget", "Budget", "number"],
      ["description", "Description", "textarea"],
    ],
    address: [
      ["propertyAddress", "Property address"],
      ["city", "City"],
      ["state", "State"],
      ["pincode", "Pincode"],
      ["location", "Landmark"],
    ],
    additional: [
      ["possessionMonth", "Timeline / Possession"],
      ["bhkType", "BHK Type"],
      ["stage", "Lead Status"],
      ["estBudget", "Initial Budget", "number"],
    ],
  };

  const detailsEditTitle = {
    contact: "Contact Details",
    project: "Project Details",
    address: "Address Details",
    additional: "Additional Fields",
  };

  const handleToggleConvert = async () => {
    if (converting || !clientId) return;
    setConverting(true);
    const newStatus = isConverted ? "prospect" : "active";
    try {
      await api.put(`/clients/${clientId}`, { status: newStatus });
      setIsConverted(!isConverted);
      setProject((prev) => {
        if (!prev || !prev.client || typeof prev.client !== "object") return prev;
        return {
          ...prev,
          client: {
            ...prev.client,
            status: newStatus,
          },
        };
      });
      if (["invoices", "financials"].includes(activeTab) && newStatus !== "active") {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", "files");
            return next;
          },
          { replace: true }
        );
      }
    } catch (e) {
      console.error(e);
      alert("Could not update conversion status");
    } finally {
      setConverting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fff" }}>
      {detailsEdit && (
        <div
          onClick={() => !detailsSaving && setDetailsEdit(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2500,
            background: "rgba(17,24,39,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <form
            onSubmit={submitDetailsEdit}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(560px, 94vw)",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 24px 70px rgba(17,24,39,0.22)",
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid #E5E7EB" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937" }}>
                Edit {detailsEditTitle[detailsEdit]}
              </div>
              <button
                type="button"
                onClick={() => setDetailsEdit(null)}
                disabled={detailsSaving}
                style={{ border: "none", background: "transparent", color: "#6B7280", fontSize: 20, cursor: "pointer" }}
              >
                x
              </button>
            </div>
            <div style={{ padding: 16, display: "grid", gap: 12 }}>
              {(detailsEditFields[detailsEdit] || []).map(([field, label, type = "text"]) => (
                <label key={field} style={{ display: "grid", gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#6B7280" }}>{label}</span>
                  {type === "textarea" ? (
                    <textarea
                      value={detailsForm[field] || ""}
                      onChange={(e) => updateDetailsField(field, e.target.value)}
                      rows={4}
                      style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 10px", fontSize: 13, resize: "vertical" }}
                    />
                  ) : (
                    <input
                      type={type}
                      value={detailsForm[field] || ""}
                      onChange={(e) => updateDetailsField(field, e.target.value)}
                      style={{ border: "1px solid #D1D5DB", borderRadius: 8, padding: "9px 10px", fontSize: 13 }}
                    />
                  )}
                </label>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "12px 16px", borderTop: "1px solid #E5E7EB" }}>
              <button
                type="button"
                onClick={() => setDetailsEdit(null)}
                disabled={detailsSaving}
                style={{ padding: "8px 14px", border: "1px solid #D1D5DB", borderRadius: 8, background: "#fff", color: "#374151", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={detailsSaving}
                style={{ padding: "8px 16px", border: "none", borderRadius: 8, background: "#976793", color: "#fff", fontWeight: 700, cursor: detailsSaving ? "wait" : "pointer" }}
              >
                {detailsSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
      {/* Header */}
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #E5E7EB" }}>
        <button
          onClick={() => navigate("/projects")}
          style={{
            background: "none",
            border: "none",
            color: "#9CA3AF",
            cursor: "pointer",
            marginBottom: 10,
            padding: 0,
            fontSize: 12,
          }}
        >
          ← Back to Projects
        </button>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1F2937" }}>{project.title}</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 3, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span>Client: {project.client?.name || project.clientName || "—"}</span>
              {clientId && (
                <button
                  onClick={() => navigate(`/clients/${clientId}?from=project`)}
                  style={{
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: 6,
                    color: "#374151",
                    cursor: "pointer",
                    padding: "2px 8px",
                    fontSize: 11,
                  }}
                >
                  Edit
                </button>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isConverted ? (
                  <button
                    onClick={handleToggleConvert}
                    title="Click to mark client as not converted"
                    disabled={converting}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      border: "none",
                      background: "#10B981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: converting ? "wait" : "pointer",
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </button>
                ) : (
                  <div
                    onClick={handleToggleConvert}
                    title="Toggle to convert client"
                    style={{
                      width: 36,
                      height: 20,
                      borderRadius: 10,
                      background: "#D1D5DB",
                      cursor: converting ? "wait" : "pointer",
                      position: "relative",
                      transition: "background 0.2s",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 2,
                        left: 2,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                      }}
                    />
                  </div>
                )}
                <div style={{ position: "relative" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowInfo((s) => !s);
                    }}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "1px solid #3B82F6",
                      background: "#F7F1F7",
                      color: "#3B82F6",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: "16px",
                      padding: 0,
                    }}
                  >
                    i
                  </button>
                  {showInfo && (
                    <div
                      style={{
                        position: "absolute",
                        top: 22,
                        left: 0,
                        background: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
                        padding: "8px 10px",
                        fontSize: 11,
                        color: "#374151",
                        whiteSpace: "nowrap",
                        zIndex: 20,
                      }}
                    >
                      Client is {isConverted ? "Converted" : "Not Converted"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="project-detail-tabs" style={{ borderBottom: "1px solid #E5E7EB", background: "#fff", overflowX: "auto" }}>
        <div style={{ display: "flex", minWidth: "max-content" }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                if (t.requiresConverted && !isConverted) return;
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", t.id);
                    return next;
                  },
                  { replace: true }
                );
              }}
              style={{
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: activeTab === t.id ? 600 : 400,
                color:
                  t.requiresConverted && !isConverted
                    ? "#C4C9D4"
                    : activeTab === t.id
                      ? "#976793"
                      : "#6B7280",
                background: "transparent",
                border: "none",
                borderBottom: activeTab === t.id ? "2px solid #976793" : "2px solid transparent",
                cursor: t.requiresConverted && !isConverted ? "not-allowed" : "pointer",
                marginBottom: -1,
                whiteSpace: "nowrap",
                opacity: t.requiresConverted && !isConverted ? 0.6 : 1,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="project-detail-content" style={{ flex: 1, overflowY: "auto", padding: "16px 20px", background: "#F8F9FB" }}>
        {activeTab === "files" && clientId && <FilesTab clientId={clientId} />}
        {activeTab === "notes" && clientId && <NotesTab clientId={clientId} projectId={project?._id} />}
        {activeTab === "quotes" && clientId && (
          <QuotesTab clientId={clientId} projectId={project._id} isConverted={true} />
        )}
        {activeTab === "agreement" && clientId && (
          <AgreementTab clientId={clientId} project={project} />
        )}
        {activeTab === "orders" && clientId && <OrdersTab clientId={clientId} projectId={project._id} />}
        {activeTab === "inventory" && clientId && (
          <ClientInventoryTab clientId={clientId} projectId={project?._id} />
        )}
        {activeTab === "invoices" && clientId && (
          <InvoicesTab clientId={clientId} client={project.client} project={project} />
        )}
        {activeTab === "financials" && clientId && <FinancialsTab clientId={clientId} projectId={project._id} />}
        {activeTab === "details" && (
          <div style={{ maxWidth: 980 }}>
            {[
              {
                id: "contact",
                title: "Contact Details",
                fields: [
                  ["Name", project.client?.name || project.clientName || "N/A"],
                  ["Email", project.client?.email || project.email || "N/A"],
                  ["Phone", project.client?.phone || project.phone || "N/A"],
                ],
              },
              {
                id: "project",
                title: "Project Details",
                fields: [
                  ["Created on", project.createdAt ? new Date(project.createdAt).toLocaleDateString("en-IN") : "N/A"],
                  ["Last updated on", project.updatedAt ? new Date(project.updatedAt).toLocaleDateString("en-IN") : "N/A"],
                  [
                    "Budget",
                    (() => {
                      const bt = project.budget?.total;
                      if (bt != null && Number(bt) > 0) return `₹ ${Number(bt).toLocaleString("en-IN")}`;
                      const est = project.estBudget ?? project.client?.estBudget;
                      if (est != null && Number(est) > 0) return `₹ ${Number(est).toLocaleString("en-IN")}`;
                      return "—";
                    })(),
                  ],
                  ["Description", project.description || "N/A"],
                ],
              },
              {
                id: "address",
                title: "Address Details",
                fields: [
                  ["Property address", project.propertyAddress || project.client?.propertyAddress || "N/A"],
                  ["City", project.city || project.client?.address?.city || "N/A"],
                  ["State", project.state || project.client?.address?.state || "N/A"],
                  ["Pincode", project.client?.address?.pincode || "N/A"],
                  ["Landmark", project.client?.address?.location || "N/A"],
                ],
              },
            ].map((section) => (
              <div key={section.title} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #E5E7EB" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>{section.title}</span>
                  <button
                    type="button"
                    onClick={() => openDetailsEdit(section.id)}
                    style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #E5E7EB", borderRadius: 8, background: "#976793", color: "#fff", padding: "4px 12px", fontSize: 12, cursor: "pointer" }}
                  >
                    ✏ Edit
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, padding: "12px 16px" }}>
                  {section.fields.map(([label, value]) => (
                    <div key={label} style={{ padding: "6px 0" }}>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 13, color: "#374151" }}>{value || "N/A"}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #E5E7EB" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>Documents</span>
                <button style={{ background: "none", border: "none", color: "#976793", fontSize: 12, cursor: "pointer" }}>+ Add Documents</button>
              </div>
              <div style={{ padding: "12px 16px", color: "#9CA3AF", fontSize: 13 }}>No documents found for this project.</div>
            </div>

            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #E5E7EB" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1F2937" }}>Additional Fields</span>
                <button
                  type="button"
                  onClick={() => openDetailsEdit("additional")}
                  style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #E5E7EB", borderRadius: 8, background: "#976793", color: "#fff", padding: "4px 12px", fontSize: 12, cursor: "pointer" }}
                >
                  ✏ Edit
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0, padding: "12px 16px" }}>
                {(() => {
                  const possession = project.possessionMonth || project.client?.possessionMonth;
                  const start = project.startDate ? new Date(project.startDate).toLocaleDateString("en-IN") : null;
                  const end = project.expectedEndDate ? new Date(project.expectedEndDate).toLocaleDateString("en-IN") : null;
                  let timeline = "—";
                  if (start || end) timeline = [start || "—", end || "—"].join(" → ");
                  else if (possession) timeline = possession;
                  const bhk = project.bhkType || project.client?.bhkType;
                  const leadStatus = project.stage || project.client?.stage;
                  const est = project.estBudget ?? project.client?.estBudget;
                  const initialBudget =
                    est != null && Number(est) > 0 ? `₹ ${Number(est).toLocaleString("en-IN")}` : "—";
                  return [
                    ["Timeline", timeline],
                    ["BHK Type", bhk || "—"],
                    ["Lead Status", leadStatus || "—"],
                    ["Initial Budget", initialBudget],
                  ].map(([label, value]) => (
                    <div key={label} style={{ padding: "6px 0" }}>
                      <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 2 }}>{label}</div>
                      <div style={{ fontSize: 13, color: "#374151" }}>{value || "—"}</div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
        )}
        {!clientId && (
          <div style={{ color: "#9CA3AF" }}>
            This project has no linked client. Please set a client first.
          </div>
        )}
      </div>
    </div>
  );
}
